import { createFeatureSelector, createSelector } from '@ngrx/store';
import { StateSlice } from '../state-slice';
import { IBillingState } from './billing-state';
import { Billing } from '@core/models/billing/billing';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import { SubscriptionType } from '@core/models/billing/subscription-type';
import { getInvoicesEntities } from '../shared/entities/entities-selectors';
import { BillingInvoice } from '@core/models/billing/billing-invoice';
import { getCompanyFeatures } from '../session/session-selectors';
import { SubscriptionTier } from '@core/models/billing/subscription-tier';

export const selectBillingState = createFeatureSelector<IBillingState>(StateSlice.BILLING);

export const getBillingState = createSelector(
    selectBillingState,
    (state: IBillingState) => state
)

export const getBilling = createSelector(
    getBillingState,
    (state: IBillingState) => state.billing
);

export const isBillingFetching = createSelector(
    getBillingState,
    (state: IBillingState) => state.isBillingFetching
);

export const getBillingKey = createSelector(
    getBillingState,
    (state: IBillingState) => state.key
);

export const isBillingKeyFetching = createSelector(
    getBillingState,
    (state: IBillingState) => state.isKeyFetching
);

export const getBillingEstimate = createSelector(
    getBillingState,
    (state: IBillingState) => state.estimate
);

export const isBillingEstimateFetching = createSelector(
    getBillingState,
    (state: IBillingState) => state.isEstimateFetching
);

export const getBillingInvoicesIds = createSelector(
    getBillingState,
    (state: IBillingState) => { 
        return state.invoices.ids 
    }
);

export const getIHDSubscription = createSelector(
    getBilling,
    (billing: Billing): BillingSubscription => {
        if (billing && billing.subscriptions) {
            return billing.subscriptions.find(
                (subscription: BillingSubscription) =>
                    subscription.metadata.type === SubscriptionType.IHD
            );
        }

        return null;
    }
);
export const getSubscription = createSelector(
    getBilling,
    (billing: Billing): BillingSubscription => {
        if (billing && billing.subscriptions) {
            return billing.subscriptions.find(
                (subscription: BillingSubscription) =>
                    subscription.metadata.type ===
                    SubscriptionType.TNA_COMPLIANCE
            );
        }

        return null;
    }
);
export const getSubscriptions = createSelector(
    getBilling,
    (billing: Billing): BillingSubscription[] => billing?.subscriptions || []
);

export const isBillingInvoicesFetching = createSelector(
    getBillingState,
    (state: IBillingState): boolean => state.invoices.isFetching
);

export const isBillingInvoicesFetchingMore = createSelector(
    getBillingState,
    (state: IBillingState): boolean => state.invoices.isFetchingMore
);
export const getBillingInvoices = createSelector(
    getBillingInvoicesIds,
    getInvoicesEntities,
    (invoiceIds: string[], invoices: { [id: string]: BillingInvoice }) => {
        return invoiceIds.map((invoiceId: string) => {
            return invoices[invoiceId]
        });
    }
);

export const getActiveTier = createSelector(
    [getCompanyFeatures, getBilling],
    (companyFeatures, billing) => {
        let activeTier: SubscriptionTier = SubscriptionTier.FREE;
        if (billing && billing.tier) {
            activeTier = billing.tier;
        } else if (companyFeatures?.tier) {
            activeTier = companyFeatures.tier;
        }

        return activeTier;
    }
);

export const getBillingTier = createSelector(
    [getSubscription, getActiveTier],
    (subscription: BillingSubscription, activeTier: SubscriptionTier) => {

        let currentTier: SubscriptionTier = SubscriptionTier.FREE;
        if (activeTier === SubscriptionTier.FREEBIE) {
            currentTier = SubscriptionTier.FREEBIE;
        }

        if (subscription) {
            if (
                !subscription.isTrial &&
                !subscription.isCancelled &&
                !subscription.isCancelling
            ) {
                currentTier =
                    subscription.metadata && subscription.metadata.tier;
            } else if (subscription.isTrial) {
                currentTier = SubscriptionTier.FREE; // Basic
            }

            // Scheduled plan for next month
            if (
                (subscription.isSubscribed || subscription.isCancelling) &&
                subscription.scheduled_plan
            ) {
                currentTier = subscription.scheduled_plan.metadata.tier;
            }
        }

        return currentTier;
    }
);